import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Flex, Text, Button, Divider, Box, Spinner, Icon, VStack, Image, IconButton, } from "@chakra-ui/react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTables } from "../../hooks/useTables";
import { usePub } from "../../hooks/usePub";
import { useCustomToast } from "../useCustomToast";
import TableFilterDrawer from "./TableFilterDrawer";
import { FiFilter, FiLayout, FiPhone } from "react-icons/fi";
import UserSelectionsSummary from "../common/UserSelectionsSummary";
import ChangeSelectionsModal from "../common/ChangeSelectionsModal";
import ResponsiveModal from "../common/ResponsiveModal";
import { IMG_URL } from "../common/Constant";
import TableDesc from "./TableDesc";
import { usePubSectionImages } from "../../hooks/usePubSectionImages";
import { ChevronLeftIcon, ChevronRightIcon, } from "@chakra-ui/icons";
import { calculatePrice, getLowestCalculatedPriceByArea, getMinLadiesByArea, getRecommendedAreaByLowestPrice, generateAreaIdentifiers, } from "../../shared-utils/utils";
import TableSvgRenderer from "./TableSvgRenderer";
import { useTableLayoutSvg } from "../../hooks/useTableLayoutSvg";
import InsufficientLadiesModal from "./InsufficientLadiesModal";
const TABLE_LAYOUT_COLLAPSE_THRESHOLD = 40;
const TableArea = ({ pubId, time, peopleCount, selectedTable, setSelectedTable, femaleCount = 0, maleCount = 0, timeSlots = [], onTimeChange, }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j;
    const { eventId } = useParams();
    const customToast = useCustomToast();
    const navigate = useNavigate();
    const { data: svgUrl = "", isLoading: isSvgUrlLoading } = useTableLayoutSvg(pubId);
    const { data: pub, isLoading: isPubDetailLoading } = usePub(pubId);
    const tableLayouts = ((_b = (_a = pub === null || pub === void 0 ? void 0 : pub.data) === null || _a === void 0 ? void 0 : _a.pub_detail) === null || _b === void 0 ? void 0 : _b.tableLayout) || [];
    const { data, isLoading } = useTables(pubId, eventId, time, maleCount, femaleCount);
    const areaPriceRange = ((_c = data === null || data === void 0 ? void 0 : data.data) === null || _c === void 0 ? void 0 : _c.areaPriceRange) || {};
    const isNewTableFormat = ((_d = data === null || data === void 0 ? void 0 : data.data) === null || _d === void 0 ? void 0 : _d.newTableFormat) || false;
    const availableTablesList = ((_e = data === null || data === void 0 ? void 0 : data.data) === null || _e === void 0 ? void 0 : _e.tables) || [];
    const minLadiesRequired = getMinLadiesByArea(((_f = data === null || data === void 0 ? void 0 : data.data) === null || _f === void 0 ? void 0 : _f.tables) || []);
    const { data: areaInfo, isLoading: isSectionImagesLoading } = usePubSectionImages(pubId);
    const [searchParams, setSearchParams] = useSearchParams();
    const [availableTables, setAvailableTables] = useState([]);
    const [filteredTables, setFilteredTables] = useState([]);
    const [areaList, setAreaList] = useState([]);
    const [tableTypeList, setTableTypeList] = useState([]);
    const [seats, setSeats] = useState([]);
    const [selectedArea, setSelectedArea] = useState("");
    const [recommendedTables, setRecommendedTables] = useState([]);
    const [otherTables, setOtherTables] = useState([]);
    const [tableLayout, setTableLayout] = useState([]);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [isChangeModalOpen, setIsChangeModalOpen] = useState(false);
    const [isNestedModalOpen, setIsNestedModalOpen] = useState(false);
    const [selectedTab, setSelectedTab] = useState(() => {
        const paramTab = searchParams.get("selectedTab");
        return paramTab || "Table Layout";
    });
    const defaultPriceRange = [0, 100000];
    const [filters, setFilters] = useState({
        availability: [],
        area: [],
        tableType: [],
        tableFor: [],
        priceRange: defaultPriceRange,
        sortBy: "",
    });
    const [showBooking, setShowBooking] = useState(false);
    const [areas, setAreas] = useState([]);
    const areaRefs = useRef({});
    const tabsScrollRef = useRef(null);
    const [isScrolled, setIsScrolled] = useState(false);
    const contentRef = useRef(null);
    const touchStartX = useRef(0);
    const touchEndX = useRef(0);
    const touchStartY = useRef(0);
    const isSwiping = useRef(false);
    const [swipeOffset, setSwipeOffset] = useState(0);
    const rafId = useRef(null);
    const touchStartedInSwiper = useRef(false);
    const [isAreaModalOpen, setIsAreaModalOpen] = useState(false);
    const [modalAreaName, setModalAreaName] = useState("");
    const [isInsufficientLadiesModalOpen, setIsInsufficientLadiesModalOpen] = useState(false);
    const [insufficientLadiesData, setInsufficientLadiesData] = useState({ areaName: "", required: 0 });
    const isLayoutLoading = isSectionImagesLoading ||
        isLoading ||
        isPubDetailLoading ||
        isSvgUrlLoading;
    const recommendedAreaName = useMemo(() => {
        var _a;
        const recommendedArea = areas.find((area) => area.recommended);
        if (recommendedArea) {
            return recommendedArea.name;
        }
        return ((_a = areas[0]) === null || _a === void 0 ? void 0 : _a.name) || "";
    }, [areas]);
    const hasSvgLayout = !!svgUrl;
    const hasImageLayout = tableLayouts.length > 0;
    const handleGoToRecommendedTab = () => {
        if (!recommendedAreaName)
            return;
        setSelectedTab(recommendedAreaName);
        setSelectedArea(recommendedAreaName);
    };
    useEffect(() => {
        return () => {
            if (rafId.current) {
                cancelAnimationFrame(rafId.current);
            }
        };
    }, []);
    useEffect(() => {
        if (selectedTab && areas.length > 0) {
            // Use setTimeout to ensure DOM is updated and refs are set
            const scrollToTab = () => {
                const tabElement = areaRefs.current[selectedTab];
                if (tabElement && tabsScrollRef.current) {
                    // For "Table Layout" tab (sticky), scroll container to start
                    if (selectedTab === "Table Layout") {
                        tabsScrollRef.current.scrollTo({
                            left: 0,
                            behavior: "smooth",
                        });
                    }
                    else {
                        const container = tabsScrollRef.current;
                        const tabLeft = tabElement.offsetLeft;
                        const tabWidth = tabElement.offsetWidth;
                        const containerWidth = container.clientWidth;
                        // Calculate the position to center the tab
                        const targetScroll = Math.max(0, tabLeft - containerWidth / 2 + tabWidth / 2);
                        container.scrollTo({
                            left: targetScroll,
                            behavior: "smooth",
                        });
                    }
                }
            };
            // Try with multiple delays to ensure DOM is ready and refs are set
            // Increased delays to handle reload scenarios
            const timeout1 = setTimeout(scrollToTab, 100);
            const timeout2 = setTimeout(scrollToTab, 300);
            const timeout3 = setTimeout(scrollToTab, 500);
            return () => {
                clearTimeout(timeout1);
                clearTimeout(timeout2);
                clearTimeout(timeout3);
            };
        }
    }, [selectedTab, areas.length]);
    // Track scroll position for sticky table layout tab
    useEffect(() => {
        const checkScrollState = () => {
            if (tabsScrollRef.current) {
                const scrollLeft = tabsScrollRef.current.scrollLeft;
                // Only show icon when user has scrolled (scrollLeft > 0)
                // By default, show full tab text
                setIsScrolled(scrollLeft > TABLE_LAYOUT_COLLAPSE_THRESHOLD);
            }
        };
        const scrollContainer = tabsScrollRef.current;
        if (scrollContainer) {
            // Initialize to false (show full tab by default)
            setIsScrolled(false);
            // Listen for scroll events
            scrollContainer.addEventListener("scroll", checkScrollState);
            return () => {
                scrollContainer.removeEventListener("scroll", checkScrollState);
            };
        }
    }, [areas.length]);
    // const allTabs = [
    //   { name: "Table Layout", type: "layout" },
    //   ...areas.map((area) => ({ name: area.name, type: "area", ...area })),
    // ];
    const allTabs = useMemo(() => {
        if (hasSvgLayout) {
            return [];
        }
        if (hasImageLayout) {
            return [
                { name: "Table Layout", type: "layout", recommended: true },
                ...areas.map((area) => ({
                    name: area.name,
                    type: "area",
                    recommended: area.recommended,
                })),
            ];
        }
        return [];
    }, [hasSvgLayout, hasImageLayout, areas]);
    const currentTabIndex = allTabs.findIndex((t) => t.name === selectedTab);
    const canGoPrev = currentTabIndex > 0;
    const canGoNext = currentTabIndex < allTabs.length - 1;
    const handlePrevTab = () => {
        if (canGoPrev) {
            const prevTab = allTabs[currentTabIndex - 1];
            setSelectedTab(prevTab.name);
            if (prevTab.type === "area") {
                setSelectedArea(prevTab.name);
            }
        }
    };
    const handleNextTab = () => {
        if (canGoNext) {
            const nextTab = allTabs[currentTabIndex + 1];
            setSelectedTab(nextTab.name);
            if (nextTab.type === "area") {
                setSelectedArea(nextTab.name);
            }
        }
    };
    const [currentIndex, setCurrentIndex] = useState(0);
    const handlePrev = () => {
        setCurrentIndex((prev) => prev === 0 ? tableLayouts.length - 1 : prev - 1);
    };
    const handleNext = () => {
        setCurrentIndex((prev) => prev === tableLayouts.length - 1 ? 0 : prev + 1);
    };
    useEffect(() => {
        const allAreas = new Set([
            ...recommendedTables.map((t) => t.areaName),
            ...otherTables.map((t) => t.areaName),
        ]);
        const lowestPriceArea = getRecommendedAreaByLowestPrice(availableTablesList, femaleCount);
        const mergedAreas = Array.from(allAreas).map((name) => {
            const isRecommended = lowestPriceArea.areaName === name;
            return { name, recommended: isRecommended };
        });
        mergedAreas.sort((a, b) => {
            if (a.recommended && !b.recommended)
                return -1;
            if (!a.recommended && b.recommended)
                return 1;
            const aPrice = getLowestCalculatedPriceByArea(a.name, availableTablesList);
            const bPrice = getLowestCalculatedPriceByArea(b.name, availableTablesList);
            return aPrice - bPrice;
        });
        setAreas(mergedAreas);
        const paramArea = searchParams.get("selectedArea");
        const validParam = paramArea && mergedAreas.some((a) => a.name === paramArea);
        if (validParam) {
            setSelectedArea(paramArea);
        }
        else if (mergedAreas.length > 0) {
            setSelectedArea(mergedAreas[0].name);
        }
        else {
            setSelectedArea("");
        }
    }, [recommendedTables, otherTables, availableTablesList]);
    useEffect(() => {
        const paramArea = searchParams.get("selectedArea");
        if (selectedArea && paramArea !== selectedArea) {
            const newParams = new URLSearchParams(searchParams);
            newParams.set("selectedArea", selectedArea);
            setSearchParams(newParams, { replace: true });
        }
    }, [selectedArea, searchParams]);
    // Initialize selectedTab from URL params when areas are loaded
    useEffect(() => {
        const paramTab = searchParams.get("selectedTab");
        if (paramTab && areas.length > 0) {
            // Build allTabs to check if tab exists
            const allTabsCheck = [
                { name: "Table Layout", type: "layout" },
                ...areas.map((area) => ({ name: area.name, type: "area", ...area })),
            ];
            // Check if the tab exists in allTabs
            const tabExists = allTabsCheck.some((tab) => tab.name === paramTab);
            if (tabExists && selectedTab !== paramTab) {
                setSelectedTab(paramTab);
                // If it's an area tab, also set selectedArea
                const tab = allTabsCheck.find((t) => t.name === paramTab);
                if ((tab === null || tab === void 0 ? void 0 : tab.type) === "area") {
                    setSelectedArea(paramTab);
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [areas.length]); // Run when areas are available
    // Update URL when selectedTab changes
    useEffect(() => {
        if (selectedTab) {
            const paramTab = searchParams.get("selectedTab");
            if (paramTab !== selectedTab) {
                const newParams = new URLSearchParams(searchParams);
                newParams.set("selectedTab", selectedTab);
                setSearchParams(newParams, { replace: true });
            }
        }
    }, [selectedTab]);
    const openFilter = () => setIsFilterOpen(true);
    const closeFilter = () => setIsFilterOpen(false);
    const openChangeModal = () => setIsChangeModalOpen(true);
    const closeChangeModal = () => {
        setIsChangeModalOpen(false);
    };
    const handleTimeChange = (newTime, newMaleCount, newFemaleCount) => {
        closeChangeModal();
        navigate(-1);
        setTimeout(() => {
            if (onTimeChange) {
                onTimeChange(newTime, newMaleCount, newFemaleCount);
            }
        }, 100);
    };
    // Check if table is disabled due to female requirements
    const isTableDisabled = (table) => {
        return (table.minLadiesRequired &&
            femaleCount < table.minLadiesRequired &&
            !table.isNewTableFormat);
    };
    // Check if all tables are sold out
    const allTablesSoldOut = React.useMemo(() => {
        const allTables = [...recommendedTables, ...otherTables];
        return (allTables.length > 0 &&
            allTables.every((table) => table.availableTables === 0));
    }, [recommendedTables, otherTables, areas]);
    const categorizeTables = (tables) => {
        const recommended = [];
        const other = [];
        const safeNum = (v) => (Number.isFinite(+v) ? +v : 0);
        const getSeat = (t) => safeNum(t.seatCount);
        const getStanding = (t) => safeNum(t.standingGuestCount);
        const getTotal = (t) => getSeat(t) + getStanding(t);
        const getPrice = (t) => {
            var _a;
            return calculatePrice(safeNum(t.coverPrice), safeNum(t.entryPrice), safeNum(t.discountValue), (_a = t.discountType) !== null && _a !== void 0 ? _a : "none");
        };
        const validTables = tables.filter((t) => !isTableDisabled(t) && safeNum(t.availableTables) > 0);
        const source = validTables.length ? validTables : tables;
        const seatedFit = source.filter((t) => getSeat(t) >= peopleCount);
        const mixedFit = source.filter((t) => getSeat(t) < peopleCount && getTotal(t) >= peopleCount);
        let candidates = seatedFit.length > 0 ? seatedFit : mixedFit;
        if (candidates.length === 0) {
            return { recommended, other: [...tables] };
        }
        const enriched = candidates.map((t) => {
            const seat = getSeat(t);
            const total = getTotal(t);
            const isSeatedFit = seat >= peopleCount;
            const capacityUsed = isSeatedFit ? seat : total;
            const waste = capacityUsed - peopleCount;
            const price = getPrice(t);
            return {
                table: t,
                capacityUsed,
                waste,
                price,
                isSeatedFit,
            };
        });
        enriched.sort((a, b) => {
            if (a.waste !== b.waste)
                return a.waste - b.waste;
            if (a.price !== b.price)
                return b.price - a.price;
            return a.capacityUsed - b.capacityUsed;
        });
        const selected = enriched[0].table;
        recommended.push(selected);
        other.push(...tables.filter((t) => t !== selected));
        return { recommended, other };
    };
    // Apply filters to tables
    const applyFilters = (tables, filterState) => {
        let filtered = [...tables];
        // Filter by availability
        if (filterState.availability.length > 0) {
            filtered = filtered.filter((table) => {
                const isFastFilling = table.availableTables > 0;
                const isSoldOut = table.availableTables === 0;
                if (filterState.availability.includes("fast_filling") && isFastFilling)
                    return true;
                if (filterState.availability.includes("sold_out") && isSoldOut)
                    return true;
                return false;
            });
        }
        // Filter by area
        if (filterState.area.length > 0) {
            filtered = filtered.filter((table) => filterState.area.includes(table.areaName));
        }
        // Filter by table type
        if (filterState.tableType.length > 0) {
            filtered = filtered.filter((table) => filterState.tableType.includes(table.tableType));
        }
        // Filter by table for (seat count)
        if (filterState.tableFor.length > 0) {
            filtered = filtered.filter((table) => filterState.tableFor.includes(table.seatCount.toString()));
        }
        // Filter by price range
        if (filterState.priceRange[0] !== 0 ||
            filterState.priceRange[1] !== 100000) {
            filtered = filtered.filter((table) => {
                const price = table.price || 0;
                return (price >= filterState.priceRange[0] &&
                    price <= filterState.priceRange[1]);
            });
        }
        // Sort tables
        if (filterState.sortBy) {
            switch (filterState.sortBy) {
                case "price_low":
                    filtered.sort((a, b) => (a.price || 0) - (b.price || 0));
                    break;
                case "price_high":
                    filtered.sort((a, b) => (b.price || 0) - (a.price || 0));
                    break;
            }
        }
        return filtered;
    };
    // Set default tab when areas are available
    useEffect(() => {
        if (areas.length > 0 && selectedTab === "Table Layout") {
            // Keep "Table Layout" as default, but ensure selectedArea is set for when user switches to area tabs
            if (!selectedArea && areas.length > 0) {
                setSelectedArea(areas[0].name);
            }
        }
    }, [areas]);
    // Handle filter apply
    const handleFilterApply = (newFilters) => {
        setFilters(newFilters);
        const filtered = applyFilters(availableTables, newFilters);
        setFilteredTables(filtered);
        // Categorize filtered tables
        const { recommended, other } = categorizeTables(filtered);
        setRecommendedTables(recommended);
        setOtherTables(other);
        closeFilter();
    };
    // Handle clear all filters
    const handleClearFilters = () => {
        const clearedFilters = {
            availability: [],
            area: [],
            tableType: [],
            tableFor: [],
            priceRange: defaultPriceRange,
            sortBy: "",
        };
        setFilters(clearedFilters);
        setFilteredTables(availableTables);
        // Categorize available tables
        const { recommended, other } = categorizeTables(availableTables);
        setRecommendedTables(recommended);
        setOtherTables(other);
        closeFilter();
    };
    useEffect(() => {
        var _a, _b, _c;
        if (!isLoading && ((_c = (_b = (_a = pub === null || pub === void 0 ? void 0 : pub.data) === null || _a === void 0 ? void 0 : _a.pub_detail) === null || _b === void 0 ? void 0 : _b.tableLayout) === null || _c === void 0 ? void 0 : _c.length)) {
            setTableLayout(pub.data.pub_detail.tableLayout);
        }
    }, [isLoading, pub]);
    useEffect(() => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
        if (!isLoading && data && ((_a = data.data) === null || _a === void 0 ? void 0 : _a.tables)) {
            const maxPrice = ((_b = data.data) === null || _b === void 0 ? void 0 : _b.tables.length)
                ? Math.max(...(_d = (_c = data === null || data === void 0 ? void 0 : data.data) === null || _c === void 0 ? void 0 : _c.tables) === null || _d === void 0 ? void 0 : _d.map((table) => table.price || 0))
                : 100000;
            setFilters({
                ...filters,
                priceRange: [0, maxPrice],
            });
            const uniqueAreaNames = Array.from(new Set((_f = (_e = data === null || data === void 0 ? void 0 : data.data) === null || _e === void 0 ? void 0 : _e.tables) === null || _f === void 0 ? void 0 : _f.map((table) => table.areaName)));
            setAreaList(uniqueAreaNames);
            const uniqueTableTypes = Array.from(new Set((_h = (_g = data === null || data === void 0 ? void 0 : data.data) === null || _g === void 0 ? void 0 : _g.tables) === null || _h === void 0 ? void 0 : _h.map((table) => table.tableType)));
            setTableTypeList(uniqueTableTypes);
            const uniqueSeats = Array.from(new Set((_k = (_j = data === null || data === void 0 ? void 0 : data.data) === null || _j === void 0 ? void 0 : _j.tables) === null || _k === void 0 ? void 0 : _k.map((table) => table.seatCount))).sort((a, b) => a - b);
            setSeats(uniqueSeats);
            // Set initial available tables
            setAvailableTables((_l = data.data) === null || _l === void 0 ? void 0 : _l.tables);
            setFilteredTables((_m = data.data) === null || _m === void 0 ? void 0 : _m.tables);
            // Categorize tables into recommended and other
            const { recommended, other } = categorizeTables((_o = data.data) === null || _o === void 0 ? void 0 : _o.tables);
            setRecommendedTables(recommended);
            setOtherTables(other);
            // Set default area if available
        }
        else if ((data === null || data === void 0 ? void 0 : data.status_code) === "FORBIDDEN") {
            customToast({
                title: data.status_message,
                status: "error",
            });
            navigate(-1);
        }
    }, [isLoading, data]);
    useEffect(() => {
        const filtered = applyFilters(availableTables, filters);
        setFilteredTables(filtered);
        const { recommended, other } = categorizeTables(filtered);
        setRecommendedTables(recommended);
        setOtherTables(other);
    }, [filters, availableTables]);
    const renderTableLayout = () => {
        var _a, _b;
        if (hasSvgLayout) {
            return (_jsx(TableSvgRenderer, { svgUrl: svgUrl, sectionImages: ((_a = areaInfo === null || areaInfo === void 0 ? void 0 : areaInfo.data) === null || _a === void 0 ? void 0 : _a.map((x) => x.areaName)) || [], areaNames: generateAreaIdentifiers(availableTablesList), minLadiesRequired: minLadiesRequired, femaleCount: femaleCount, lowestPriceAreaName: getRecommendedAreaByLowestPrice(availableTablesList, femaleCount), onAreaClick: (id) => {
                    // Support both formats:
                    // New format: "SUNDECK_REGULAR" (areaName_tableType)
                    // Old format: "SUNDECK" or "SUNDECK VIP" (just areaName)
                    const clickedId = id.toUpperCase();
                    // Check if it's new format (contains underscore)
                    if (clickedId.includes("_")) {
                        // New format: SUNDECK_REGULAR
                        // Use the full ID which will be parsed in TableDesc
                        setSelectedArea(clickedId);
                        setModalAreaName(clickedId);
                    }
                    else {
                        // Old format: SUNDECK or SUNDECK VIP
                        // Use as is for backward compatibility
                        setSelectedArea(clickedId);
                        setModalAreaName(clickedId);
                    }
                    setIsAreaModalOpen(true);
                }, onInsufficientLadiesClick: (id, required) => {
                    // Handle both formats for insufficient ladies modal
                    const clickedId = id.toUpperCase();
                    if (clickedId.includes("_")) {
                        // Extract just the area name for display
                        const areaName = clickedId.split("_")[0];
                        setInsufficientLadiesData({ areaName, required });
                    }
                    else {
                        setInsufficientLadiesData({ areaName: clickedId, required });
                    }
                    setIsInsufficientLadiesModalOpen(true);
                } }));
        }
        if (hasImageLayout) {
            return (_jsxs(_Fragment, { children: [((_b = tableLayouts[currentIndex]) === null || _b === void 0 ? void 0 : _b.imageUrl) && (_jsx(Image, { src: tableLayouts[currentIndex].imageUrl, w: "full", objectFit: "contain", borderRadius: "md" })), tableLayouts.length > 1 && (_jsxs(_Fragment, { children: [_jsx(IconButton, { "aria-label": "Previous", icon: _jsx(ChevronLeftIcon, { boxSize: 8 }), position: "absolute", top: "50%", left: "2", transform: "translateY(-50%)", onClick: handlePrev, bg: "rgba(0,0,0,0.5)", color: "white" }), _jsx(IconButton, { "aria-label": "Next", icon: _jsx(ChevronRightIcon, { boxSize: 8 }), position: "absolute", top: "50%", right: "2", transform: "translateY(-50%)", onClick: handleNext, bg: "rgba(0,0,0,0.5)", color: "white" })] }))] }));
        }
        return null;
    };
    const renderAreaTabContent = () => {
        var _a;
        return (_jsx(Box, { position: "relative", children: _jsx(TableDesc, { isNewTableFormat: isNewTableFormat, areaName: selectedArea, filters: filters, isRecommended: (_a = areas.find((a) => a.name === selectedArea)) === null || _a === void 0 ? void 0 : _a.recommended, data: [...recommendedTables, ...otherTables], areaInfo: areaInfo, femaleCount: femaleCount, onProceed: (selectedTable) => {
                    setSelectedTable(selectedTable);
                } }) }));
    };
    if (isLayoutLoading) {
        return (_jsx(Flex, { w: "100%", h: "50vh", justifyContent: "center", alignItems: "center", children: _jsx(Spinner, {}) }));
    }
    return (_jsxs(Flex, { w: "100%", h: "100%", flexDir: "column", position: "relative", children: [time && (_jsxs(Flex, { justifyContent: "center", position: "fixed", top: 3, left: "50%", transform: "translateX(-50%)", zIndex: 1000, alignItems: "center", my: 1, ml: 4, children: [_jsx(UserSelectionsSummary, { time: time, maleCount: maleCount, femaleCount: femaleCount, isNewTableFormat: true, onChangeClick: openChangeModal }), !hasSvgLayout && (_jsx(Box, { minW: "40px", maxW: "40px", minH: "40px", maxH: "40px", ml: 3, bg: "#1E1E1E", border: "2px solid ", borderRadius: "full", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", _hover: { bg: "#2A2A2A" }, borderColor: Object.values(filters).some((value) => {
                            if (Array.isArray(value) && value.length === 2) {
                                const [min, max] = value;
                                return !(min === 0 && (max === 100000 || max === 0));
                            }
                            if (Array.isArray(value))
                                return value.length > 0;
                            if (typeof value === "string")
                                return value.trim() !== "";
                            return false;
                        })
                            ? "#8155FE"
                            : "#414141", onClick: openFilter, children: _jsx(Icon, { as: FiFilter, color: "white", boxSize: 4 }) }))] })), allTablesSoldOut ? (_jsxs(Flex, { w: "100%", minH: "30vh", justifyContent: "center", alignItems: "center", flexDir: "column", p: 4, mt: 4, children: [_jsx(Text, { color: "whiteAlpha.800", fontSize: "lg", fontWeight: "600", textAlign: "center", mb: 2, children: "No Tables Available" }), _jsx(Text, { color: "whiteAlpha.600", fontSize: "sm", textAlign: "center", maxW: "400px", children: "All tables are currently sold out. Please try another time slot or contact the venue directly." })] })) : recommendedTables.length > 0 || otherTables.length > 0 ? (_jsxs(Flex, { flexDir: "column", position: "relative", children: [_jsx(Box, { w: "full", overflowX: "auto", mt: 4, css: { "&::-webkit-scrollbar": { height: "0px" } }, borderBottom: "1px", borderColor: "#363636", position: "relative", zIndex: 1, style: {
                            transform: "none !important",
                            willChange: "auto",
                        }, children: _jsxs(Box, { ref: tabsScrollRef, w: "full", overflowX: "auto", css: { "&::-webkit-scrollbar": { height: "0px" } }, position: "relative", style: { transform: "none !important" }, children: [allTabs.length === 0 && (_jsx(Flex, { w: "full", justifyContent: "start", alignItems: "center", mb: 2, color: "white", children: _jsx(Text, { fontSize: "16px", fontWeight: "500", children: "Select a section" }) })), allTabs.length > 0 && (_jsx(Flex, { gap: 3, w: "max-content", position: "relative", children: allTabs.map((tab, index) => {
                                        const isActive = selectedTab === tab.name;
                                        const isTableLayout = tab.type === "layout";
                                        const areaData = areaPriceRange[tab.name];
                                        const min = areaData === null || areaData === void 0 ? void 0 : areaData.minPrice;
                                        const max = areaData === null || areaData === void 0 ? void 0 : areaData.maxPrice;
                                        let priceRange = null;
                                        if (min == null && max == null) {
                                            priceRange = null;
                                        }
                                        else if (min != null && max != null) {
                                            if (min === 0 && max === 0) {
                                                priceRange = "FREE";
                                            }
                                            else if (min === max) {
                                                priceRange = min > 0 ? `₹${min}` : null;
                                            }
                                            else if (min === 0 && max > 0) {
                                                priceRange = `Up to ₹${max}`;
                                            }
                                            else {
                                                priceRange = `₹${min} - ₹${max}`;
                                            }
                                        }
                                        else if (min != null) {
                                            priceRange = min > 0 ? `₹${min}` : null;
                                        }
                                        else if (max != null) {
                                            priceRange = max > 0 ? `Up to ₹${max}` : null;
                                        }
                                        return (_jsx(Flex, { ref: (el) => {
                                                areaRefs.current[tab.name] = el;
                                            }, flexDir: "column", justifyContent: "center", alignItems: "center", px: 5, py: 2.5, borderRadius: isActive ? "10px 10px 0 0" : 0, cursor: "pointer", color: isActive ? "white" : "#9C9C9C", minW: "fit-content", transition: "all 0.2s", _hover: { color: "white" }, onClick: () => {
                                                setSelectedTab(tab.name);
                                                if (tab.type === "area") {
                                                    setSelectedArea(tab.name);
                                                    setModalAreaName(tab.name);
                                                }
                                            }, position: isTableLayout ? "sticky" : "relative", left: isTableLayout ? -1 : "auto", zIndex: isTableLayout ? 2 : 1, bg: isTableLayout && isScrolled
                                                ? isActive
                                                    ? "linear-gradient(96.56deg, #8155FE 8.58%, #4D3398 99.26%)"
                                                    : "rgba(17, 17, 17, 1)"
                                                : isActive
                                                    ? "linear-gradient(96.56deg, #8155FE 8.58%, #4D3398 99.26%)"
                                                    : "transparent", border: isTableLayout && isScrolled
                                                ? isActive
                                                    ? "1px solid #4D3398"
                                                    : "1px solid #363636"
                                                : "none", borderRight: isTableLayout ? "1px solid #363636" : "none", children: isTableLayout && isScrolled ? (_jsx(Box, { bg: isTableLayout && isScrolled
                                                    ? isActive
                                                        ? "linear-gradient(96.56deg, #8155FE 8.58%, #4D3398 99.26%)"
                                                        : "rgba(17, 17, 17, 1)"
                                                    : isActive
                                                        ? "linear-gradient(96.56deg, #8155FE 8.58%, #4D3398 99.26%)"
                                                        : "transparent", backdropFilter: isTableLayout && isScrolled ? "blur(8px)" : "none", borderRadius: "10px 10px 0 0", children: _jsx(Icon, { as: FiLayout, boxSize: 5 }) })) : (_jsxs(_Fragment, { children: [_jsx(Text, { fontSize: "14px", fontWeight: "400", lineHeight: "18px", children: tab.name }), tab.type === "area" && (tab === null || tab === void 0 ? void 0 : tab.recommended) && (_jsxs(Flex, { justifyContent: "center", alignItems: "center", gap: "1", mt: 1, children: [_jsx(Box, { w: "6px", h: "6px", bg: "red.400", borderRadius: "full" }), _jsx(Text, { fontSize: "10px", fontWeight: "400", color: "#FFFFFF", children: "Recommended" })] })), tab.type === "area" &&
                                                        !(tab === null || tab === void 0 ? void 0 : tab.recommended) &&
                                                        priceRange && (_jsx(Text, { fontSize: "10px", fontWeight: "400", color: isActive ? "#9C9C9C" : "#6B6B6B", mt: 0.5, children: priceRange }))] })) }, tab.name));
                                    }) }))] }) }), _jsx(Box, { position: "relative", mt: 4, ref: contentRef, onTouchStart: (e) => {
                            // Check if touch started within a Swiper container
                            const target = e.target;
                            const swiperContainer = target.closest('[data-swiper-container="true"]');
                            touchStartedInSwiper.current = !!swiperContainer;
                            if (swiperContainer) {
                                // Don't handle swipe if it's within Swiper
                                return;
                            }
                            touchStartX.current = e.touches[0].clientX;
                            touchStartY.current = e.touches[0].clientY;
                            isSwiping.current = false;
                            setSwipeOffset(0);
                            if (rafId.current) {
                                cancelAnimationFrame(rafId.current);
                            }
                        }, onTouchMove: (e) => {
                            // Don't handle swipe if touch started within Swiper
                            if (touchStartedInSwiper.current) {
                                return;
                            }
                            touchEndX.current = e.touches[0].clientX;
                            const diffX = touchEndX.current - touchStartX.current;
                            const diffY = Math.abs(e.touches[0].clientY - touchStartY.current);
                            const absDiffX = Math.abs(diffX);
                            // Only consider it a swipe if horizontal movement is greater than vertical
                            if (absDiffX > diffY && absDiffX > 10) {
                                if (!isSwiping.current) {
                                    isSwiping.current = true;
                                }
                                // Use requestAnimationFrame for smooth updates
                                if (rafId.current) {
                                    cancelAnimationFrame(rafId.current);
                                }
                                rafId.current = requestAnimationFrame(() => {
                                    var _a;
                                    const containerWidth = ((_a = contentRef.current) === null || _a === void 0 ? void 0 : _a.clientWidth) || window.innerWidth;
                                    // Calculate swipe offset - limit to container width for smooth preview
                                    let clampedOffset = diffX;
                                    // Apply resistance at boundaries
                                    if (diffX > 0 && !canGoNext) {
                                        // Can't swipe past last tab
                                        clampedOffset = diffX * 0.2;
                                    }
                                    else if (diffX < 0 && !canGoPrev) {
                                        // Can't swipe past first tab
                                        clampedOffset = diffX * 0.2;
                                    }
                                    else {
                                        // Normal swipe - limit to container width for preview
                                        clampedOffset = Math.max(-containerWidth * 0.5, Math.min(containerWidth * 0.5, diffX));
                                    }
                                    setSwipeOffset(clampedOffset);
                                });
                            }
                        }, onTouchEnd: (e) => {
                            // Don't handle swipe if touch started within Swiper
                            if (touchStartedInSwiper.current) {
                                // Reset state but don't process swipe
                                touchStartX.current = 0;
                                touchEndX.current = 0;
                                touchStartY.current = 0;
                                isSwiping.current = false;
                                setSwipeOffset(0);
                                touchStartedInSwiper.current = false;
                                return;
                            }
                            if (rafId.current) {
                                cancelAnimationFrame(rafId.current);
                                rafId.current = null;
                            }
                            if (isSwiping.current) {
                                const swipeThreshold = 80;
                                const diff = touchStartX.current - touchEndX.current;
                                if (Math.abs(diff) > swipeThreshold) {
                                    if (diff > 0 && canGoNext) {
                                        // Swipe left - go to next tab
                                        handleNextTab();
                                    }
                                    else if (diff < 0 && canGoPrev) {
                                        // Swipe right - go to previous tab
                                        handlePrevTab();
                                    }
                                }
                            }
                            // Smooth reset
                            if (isSwiping.current) {
                                // Only reset if we were actually swiping
                                requestAnimationFrame(() => {
                                    setSwipeOffset(0);
                                });
                            }
                            else {
                                // Immediate reset if not swiping
                                setSwipeOffset(0);
                            }
                            touchStartX.current = 0;
                            touchEndX.current = 0;
                            touchStartY.current = 0;
                            isSwiping.current = false;
                            touchStartedInSwiper.current = false;
                        }, style: {
                            touchAction: "pan-x pan-y",
                            userSelect: "none",
                            transform: "none",
                            willChange: "auto",
                        }, children: _jsxs(Box, { position: "relative", w: "full", overflow: "visible", children: [_jsxs(Box, { w: "full", px: 2, style: {
                                        transform: swipeOffset !== 0 ? `translateX(${swipeOffset}px)` : "none",
                                        transition: swipeOffset === 0 && !isSwiping.current
                                            ? "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                                            : "none",
                                        willChange: isSwiping.current ? "transform" : "auto",
                                        isolation: "isolate",
                                    }, children: [allTabs.length > 1 && (_jsxs(_Fragment, { children: [_jsx(IconButton, { "aria-label": "Previous tab", icon: _jsx(ChevronLeftIcon, { boxSize: 4 }), onClick: handlePrevTab, isDisabled: !canGoPrev, size: "sm", borderRadius: "full", bg: canGoPrev
                                                        ? "rgba(42, 42, 42, 0.7)"
                                                        : "rgba(42, 42, 42, 0.3)", color: canGoPrev
                                                        ? "rgba(255, 255, 255, 0.9)"
                                                        : "rgba(255, 255, 255, 0.4)", backdropFilter: "blur(8px)", _hover: canGoPrev
                                                        ? {
                                                            bg: "rgba(129, 85, 254, 0.6)",
                                                            transform: "translateY(-50%) scale(1.05)",
                                                        }
                                                        : {}, _active: canGoPrev
                                                        ? { transform: "translateY(-50%) scale(0.95)" }
                                                        : {}, transition: "all 0.2s ease", minW: "36px", h: "36px", position: "fixed", left: 4, top: "65%", transform: "translateY(-50%)", zIndex: 100, opacity: canGoPrev ? 0.85 : 0.3 }), _jsx(IconButton, { "aria-label": "Next tab", icon: _jsx(ChevronRightIcon, { boxSize: 4 }), onClick: handleNextTab, isDisabled: !canGoNext, size: "sm", borderRadius: "full", bg: canGoNext
                                                        ? "rgba(42, 42, 42, 0.7)"
                                                        : "rgba(42, 42, 42, 0.3)", color: canGoNext
                                                        ? "rgba(255, 255, 255, 0.9)"
                                                        : "rgba(255, 255, 255, 0.4)", backdropFilter: "blur(8px)", _hover: canGoNext
                                                        ? {
                                                            bg: "rgba(129, 85, 254, 0.6)",
                                                            transform: "translateY(-50%) scale(1.05)",
                                                        }
                                                        : {}, _active: canGoNext
                                                        ? { transform: "translateY(-50%) scale(0.95)" }
                                                        : {}, transition: "all 0.2s ease", minW: "36px", h: "36px", position: "fixed", right: 4, top: "65%", transform: "translateY(-50%)", zIndex: 100, opacity: canGoNext ? 0.85 : 0.3 })] })), selectedTab === "Table Layout" ? (_jsx(_Fragment, { children: renderTableLayout() })) : (_jsx(_Fragment, { children: renderAreaTabContent() }))] }), swipeOffset !== 0 && (_jsxs(_Fragment, { children: [swipeOffset > 20 && canGoNext && (_jsx(Box, { position: "absolute", top: 0, left: "100%", w: "full", h: "full", style: {
                                                transform: `translateX(${swipeOffset - (((_g = contentRef.current) === null || _g === void 0 ? void 0 : _g.clientWidth) || 0)}px)`,
                                                opacity: Math.min(1, Math.abs(swipeOffset) / 100),
                                            }, pointerEvents: "none", children: (() => {
                                                var _a;
                                                const nextTab = allTabs[currentTabIndex + 1];
                                                if ((nextTab === null || nextTab === void 0 ? void 0 : nextTab.type) === "layout") {
                                                    return (_jsx(Box, { px: 4, children: ((_a = tableLayouts === null || tableLayouts === void 0 ? void 0 : tableLayouts[0]) === null || _a === void 0 ? void 0 : _a.imageUrl) && (_jsx(Image, { src: tableLayouts[0].imageUrl, w: "full", objectFit: "contain", borderRadius: "md", opacity: "0.8" })) }));
                                                }
                                                else if (nextTab) {
                                                    return (_jsx(Box, { px: 4, children: _jsx(TableDesc, { isNewTableFormat: isNewTableFormat, areaName: nextTab.name, filters: filters, isRecommended: nextTab.recommended, data: [...recommendedTables, ...otherTables], areaInfo: areaInfo, femaleCount: femaleCount, onProceed: () => { } }) }));
                                                }
                                                return null;
                                            })() })), swipeOffset < -20 && canGoPrev && (_jsx(Box, { position: "absolute", top: 0, right: "100%", w: "full", h: "full", style: {
                                                transform: `translateX(${swipeOffset + (((_h = contentRef.current) === null || _h === void 0 ? void 0 : _h.clientWidth) || 0)}px)`,
                                                opacity: Math.min(1, Math.abs(swipeOffset) / 100),
                                            }, pointerEvents: "none", children: (() => {
                                                var _a;
                                                const prevTab = allTabs[currentTabIndex - 1];
                                                if ((prevTab === null || prevTab === void 0 ? void 0 : prevTab.type) === "layout") {
                                                    return (_jsx(Box, { px: 4, children: ((_a = tableLayouts === null || tableLayouts === void 0 ? void 0 : tableLayouts[0]) === null || _a === void 0 ? void 0 : _a.imageUrl) && (_jsx(Image, { src: tableLayouts[0].imageUrl, w: "full", objectFit: "contain", borderRadius: "md", opacity: "0.8" })) }));
                                                }
                                                else if (prevTab) {
                                                    return (_jsx(Box, { px: 4, children: _jsx(TableDesc, { isNewTableFormat: isNewTableFormat, areaName: prevTab.name, filters: filters, isRecommended: prevTab.recommended, data: [...recommendedTables, ...otherTables], areaInfo: areaInfo, femaleCount: femaleCount, onProceed: () => { } }) }));
                                                }
                                                return null;
                                            })() }))] }))] }) })] })) : (_jsx(Flex, { w: "100%", minH: "30vh", justifyContent: "center", alignItems: "center", flexDir: "column", p: 2, children: pubId.toString() === "866" ? (_jsxs(VStack, { mt: 8, spacing: 6, w: "100%", maxW: "400px", bg: "linear-gradient(135deg, rgba(132, 81, 208, 0.15) 0%, rgba(132, 81, 208, 0.05) 100%)", borderRadius: "20px", border: "1px solid", borderColor: "rgba(132, 81, 208, 0.3)", py: 6, px: 4, boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)", children: [_jsxs(VStack, { spacing: 3, children: [_jsx(Box, { bg: "rgba(132, 81, 208, 0.2)", borderRadius: "full", p: 4, children: _jsx(Image, { src: `${IMG_URL}/icons/table.svg`, w: "40px", h: "40px" }) }), _jsx(Text, { color: "white", fontSize: "md", fontWeight: "600", textAlign: "center", children: "Reserve Your Table" }), _jsx(Text, { color: "whiteAlpha.700", fontSize: "xs", textAlign: "center", lineHeight: "1.6", children: "No tables available online at the moment. Please reach out to the venue directly for reservations." })] }), _jsx(Divider, { borderColor: "whiteAlpha.300" }), _jsxs(VStack, { spacing: 3, w: "100%", children: [_jsx(Text, { color: "whiteAlpha.600", fontSize: "xs", fontWeight: "500", children: "CALL US ON" }), _jsxs(VStack, { spacing: 2.5, w: "100%", children: [_jsx(Button, { as: "a", href: "tel:7788999911", w: "100%", h: "48px", bg: "rgba(132, 81, 208, 0.1)", border: "0.5px solid", borderColor: "rgba(132, 81, 208, 0.4)", borderRadius: "10px", color: "white", fontSize: "sm", fontWeight: "400", leftIcon: _jsx(Icon, { as: FiPhone, boxSize: 3.5 }), _hover: {
                                                bg: "rgba(132, 81, 208, 0.2)",
                                                borderColor: "#8451D0",
                                                transform: "translateY(-1px)",
                                            }, _active: {
                                                bg: "rgba(132, 81, 208, 0.25)",
                                                transform: "translateY(0)",
                                            }, transition: "all 0.2s ease", children: "7788999911" }), _jsx(Button, { as: "a", href: "tel:7788999922", w: "100%", h: "48px", bg: "rgba(132, 81, 208, 0.1)", border: "0.5px solid", borderColor: "rgba(132, 81, 208, 0.4)", borderRadius: "10px", color: "white", fontSize: "sm", fontWeight: "400", leftIcon: _jsx(Icon, { as: FiPhone, boxSize: 3.5 }), _hover: {
                                                bg: "rgba(132, 81, 208, 0.2)",
                                                borderColor: "#8451D0",
                                                transform: "translateY(-1px)",
                                            }, _active: {
                                                bg: "rgba(132, 81, 208, 0.25)",
                                                transform: "translateY(0)",
                                            }, transition: "all 0.2s ease", children: "7788999922" })] })] })] })) : (_jsx(Text, { color: "whiteAlpha.600", fontSize: "sm", p: 4, children: "No tables found matching your criteria" })) })), (availableTables === null || availableTables === void 0 ? void 0 : availableTables.length) > 0 && (_jsx(TableFilterDrawer, { isOpen: isFilterOpen, onClose: closeFilter, tables: availableTables, areas: areaList, tableTypes: tableTypeList, seatCounts: seats, initialFilters: filters, onApply: handleFilterApply, onClearAll: handleClearFilters })), isChangeModalOpen && (_jsx(ResponsiveModal, { isOpen: isChangeModalOpen, onClose: closeChangeModal, paramKey: "change-selections-modal", header: "", showCloseButton: false, bg: "#212121", color: "white", size: "md", children: _jsx(ChangeSelectionsModal, { isOpen: isChangeModalOpen, onClose: closeChangeModal, onApply: handleTimeChange, currentTime: time || "", currentMaleCount: maleCount, currentFemaleCount: femaleCount, timeSlots: timeSlots }) })), isAreaModalOpen && (_jsx(ResponsiveModal, { isOpen: isAreaModalOpen, onClose: () => setIsAreaModalOpen(false), showCloseButton: hasSvgLayout ? false : true, paramKey: "area-modal", height: "80vh", size: "xl", bg: "#212121", color: "white", mode: "table_layout", topText: "Show Layout", children: _jsx(TableDesc, { isNewTableFormat: isNewTableFormat, areaName: modalAreaName, filters: filters, isRecommended: (_j = areas.find((a) => a.name === modalAreaName)) === null || _j === void 0 ? void 0 : _j.recommended, data: [...recommendedTables, ...otherTables], areaInfo: areaInfo, areaPriceRange: areaPriceRange, femaleCount: femaleCount, onProceed: (selectedTable) => {
                        setSelectedTable(selectedTable);
                        setIsAreaModalOpen(false);
                    }, isSvgLayout: hasSvgLayout }) })), isInsufficientLadiesModalOpen && (_jsx(InsufficientLadiesModal, { isOpen: isInsufficientLadiesModalOpen, onClose: () => {
                    setIsInsufficientLadiesModalOpen(false);
                }, areaName: insufficientLadiesData.areaName, requiredLadies: insufficientLadiesData.required, currentFemaleCount: femaleCount, currentMaleCount: maleCount, onEditPax: () => {
                    navigate(-1);
                    setIsInsufficientLadiesModalOpen(false);
                    setIsNestedModalOpen(true);
                    openChangeModal();
                } }))] }));
};
export default TableArea;
